/**
 * GoldHoldingsTab.jsx — Gold / Commodities portfolio tab.
 *
 * Displays SGBs, Gold ETFs, and gold-linked instruments
 * with overview cards, holdings table, and XIRR.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    getPortfolioOverview,
    getPortfolioXIRR,
    getHoldings,
    getSyncStatus,
    triggerSync,
} from '../../services/portfolioService';

/* ── helpers (shared with PortfolioPage) ── */
const fmt = (n, decimals = 2) => {
    if (n == null || isNaN(n)) return 'N/A';
    return Number(n).toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};
const fmtCurrency = (n) => (n == null || isNaN(n) ? 'N/A' : `₹${fmt(n)}`);
const fmtPct = (n) => (n == null || isNaN(n) ? 'N/A' : `${fmt(n)}%`);
const pnlClass = (n) => (n > 0 ? 'pnl-positive' : n < 0 ? 'pnl-negative' : '');
const pnlKpiClass = (n) => (n > 0 ? 'portfolio-kpi-value--positive' : n < 0 ? 'portfolio-kpi-value--negative' : '');
const relativeTime = (d) => {
    if (!d) return '';
    const ms = Date.now() - new Date(d).getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
};

const LANE = 'GOLD_COMMODITIES';

const GoldHoldingsTab = () => {
    const [overview, setOverview] = useState(null);
    const [xirr, setXirr] = useState(null);
    const [holdings, setHoldings] = useState([]);
    const [syncStatus, setSyncStatus] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [ov, xi, hld, ss] = await Promise.allSettled([
                getPortfolioOverview(LANE),
                getPortfolioXIRR(LANE),
                getHoldings(LANE),
                getSyncStatus(),
            ]);
            if (ov.status === 'fulfilled') setOverview(ov.value?.data ?? ov.value);
            if (xi.status === 'fulfilled') setXirr(xi.value?.data ?? xi.value);
            if (hld.status === 'fulfilled') setHoldings(hld.value?.data ?? hld.value ?? []);
            if (ss.status === 'fulfilled') setSyncStatus(ss.value?.data ?? ss.value);
        } catch { /* ignore */ }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleSync = async () => {
        setSyncing(true);
        try {
            await triggerSync();
            setTimeout(load, 3000);
        } catch { /* ignore */ }
        setSyncing(false);
    };

    if (loading) return <div className="portfolio-loading">Loading Gold &amp; Commodities…</div>;

    return (
        <>
            {/* Sync bar */}
            <div className="portfolio-sync-bar">
                <span className={`sync-status-dot sync-status-dot--${syncStatus?.overallHealth ?? 'disconnected'}`} />
                <span className="sync-label">
                    {syncStatus?.lastSyncAt ? `Last synced ${relativeTime(syncStatus.lastSyncAt)}` : 'Not synced yet'}
                </span>
                <button className="sync-btn" onClick={handleSync} disabled={syncing}>
                    {syncing ? 'Syncing…' : '⟳ Sync now'}
                </button>
            </div>

            {/* KPI cards */}
            {overview ? (
                <div className="portfolio-kpi-row">
                    <div className="portfolio-kpi">
                        <div className="portfolio-kpi-label">Current Value</div>
                        <div className="portfolio-kpi-value">{fmtCurrency(overview.currentValue)}</div>
                    </div>
                    <div className="portfolio-kpi">
                        <div className="portfolio-kpi-label">Invested Value</div>
                        <div className="portfolio-kpi-value">{fmtCurrency(overview.totalCost)}</div>
                    </div>
                    <div className="portfolio-kpi">
                        <div className="portfolio-kpi-label">Total P&L</div>
                        <div className={`portfolio-kpi-value ${pnlKpiClass(overview.totalPnl)}`}>{fmtCurrency(overview.totalPnl)}</div>
                        <div className="portfolio-kpi-sub">{fmtPct(overview.totalPnlPct)}</div>
                    </div>
                    <div className="portfolio-kpi">
                        <div className="portfolio-kpi-label">Day P&L</div>
                        <div className={`portfolio-kpi-value ${pnlKpiClass(overview.dayPnl)}`}>{fmtCurrency(overview.dayPnl)}</div>
                        <div className="portfolio-kpi-sub">{fmtPct(overview.dayPnlPct)}</div>
                    </div>
                    {xirr?.portfolioXIRR != null && (
                        <div className="portfolio-kpi">
                            <div className="portfolio-kpi-label">Gold XIRR</div>
                            <div className={`portfolio-kpi-value ${pnlKpiClass(xirr.portfolioXIRR)}`}>{fmtPct(xirr.portfolioXIRR)}</div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="portfolio-empty">
                    <div className="portfolio-empty-icon">🥇</div>
                    <div className="portfolio-empty-title">No gold / commodity holdings</div>
                    <p>SGBs and Gold ETFs from your broker will appear here after a sync.</p>
                </div>
            )}

            {/* Holdings table */}
            {holdings.length > 0 && (
                <section className="portfolio-section portfolio-section--full">
                    <h3 className="portfolio-section-title">Gold &amp; Commodities Holdings</h3>
                    <div className="holdings-table-wrap">
                        <table className="holdings-table">
                            <thead>
                                <tr>
                                    <th>Symbol</th>
                                    <th className="text-right">Qty / Units</th>
                                    <th className="text-right">Avg Cost</th>
                                    <th className="text-right">Last Price</th>
                                    <th className="text-right">Value at Cost</th>
                                    <th className="text-right">Market Value</th>
                                    <th className="text-right">Abs P&L</th>
                                    <th className="text-right">P&L %</th>
                                    <th className="text-right">Weight</th>
                                    <th className="text-right">XIRR</th>
                                </tr>
                            </thead>
                            <tbody>
                                {holdings.map((h) => {
                                    const valueAtCost = h.valueAtCost ?? ((h.quantity || 0) * (h.avgCost || 0));
                                    const absPnl = h.unrealizedPnl ?? ((h.marketValue ?? 0) - valueAtCost);
                                    const pnlPct = valueAtCost ? (absPnl / valueAtCost) * 100 : null;

                                    return (
                                        <tr key={h.instrumentId}>
                                            <td style={{ fontWeight: 600 }}>
                                                {h.name || h.instrumentId}
                                                <span className="lane-pill lane-pill--gold" title="Gold / Commodities">Au</span>
                                            </td>
                                            <td className="text-right">{fmt(h.quantity, h.quantity % 1 === 0 ? 0 : 3)}</td>
                                            <td className="text-right">{fmtCurrency(h.avgCost)}</td>
                                            <td className="text-right">{fmtCurrency(h.lastPrice)}</td>
                                            <td className="text-right">{fmtCurrency(valueAtCost)}</td>
                                            <td className="text-right">{fmtCurrency(h.marketValue)}</td>
                                            <td className={`text-right ${pnlClass(absPnl)}`}>{fmtCurrency(absPnl)}</td>
                                            <td className={`text-right ${pnlClass(pnlPct)}`}>{fmtPct(pnlPct)}</td>
                                            <td className="text-right">{fmtPct(h.globalWeightPct ?? h.weight)}</td>
                                            <td className={`text-right ${pnlClass(h.xirr)}`}>{h.xirr != null ? fmtPct(h.xirr) : 'N/A'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}
        </>
    );
};

export default GoldHoldingsTab;
